/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IModuleStateManager, IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import classname from 'classnames';
import get from 'lodash/get';
import { computed, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ICheckoutSectionContainerData } from './checkout-section-container.data';
import { ICheckoutSectionContainerProps } from './checkout-section-container.props.autogenerated';
import LoadingComponent from './components/loading';

import ReCAPTCHA from 'react-google-recaptcha';
import * as MsDyn365 from '@msdyn365-commerce/core';
import FingerprintJS, { Agent as PublicAgent } from '@fingerprintjs/fingerprintjs-pro';

export interface ICheckoutSectionContainerModuleProps extends ICheckoutSectionContainerProps<ICheckoutSectionContainerData>, IModuleStateProps {
    enableControl: boolean;
}

export enum PaymentType {
    Adyen = 'checkout-payment-instrument',
    PayByAccount = 'citta-checkout-customer-account-payment'
}
export interface ICheckoutSectionContainerItem {
    id: string;
    loading?: React.ReactNode;
    itemProps: INodeProps;
    item: React.ReactNode;
    accordion: React.ReactNode
}

export interface ICheckoutSectionContainerState {
    isCollapse?: boolean;
    reCaptchaChecked: boolean;
    rCCError: boolean;
    activeAccordion: string;
    fingerPrintError: boolean;
    fpClient: PublicAgent | null;
    requestId:string,
    attemptError: boolean;
}

export interface ICheckoutSectionContainerViewProps extends ICheckoutSectionContainerProps<{}> {
    className: string;
    checkoutPlainContainerProps: IModuleProps;
    items: ICheckoutSectionContainerItem[];
    reCaptcha: React.ReactNode;
    fingerPrintError: React.ReactNode;
    attemptError: React.ReactNode;
}

const paymentInstrumentModuleId = 'payment-instrument';

const zeroAmount = 0;
/**
 *
 * CheckoutPlainContainer component
 * @extends {React.Component<ICheckoutSectionContainerModuleProps<ICheckoutSectionContainerData>>}
 */
@observer
export class CheckoutPlainContainer extends React.Component<ICheckoutSectionContainerModuleProps, ICheckoutSectionContainerState> {
    @computed public get amountDue(): number {
        const {
            data: { checkout }
        } = this.props;
        if (!checkout?.result) {
            return 0;
        }

        const checkoutResult = checkout.result;
        const cart = checkoutResult.checkoutCart?.cart;
        if (!cart || !cart.CartLines || !ArrayExtensions.hasElements(cart.CartLines)) {
            return 0;
        }

        return cart.TotalAmount ?? zeroAmount;
    }

    public constructor(props: ICheckoutSectionContainerModuleProps) {
        super(props);
        this.state = {
            isCollapse: false,
            activeAccordion: PaymentType.PayByAccount,
            reCaptchaChecked: false,
            rCCError: false,
            fingerPrintError: false,
            fpClient:null,
            requestId: '',
            attemptError: false,
        };

    }

    private _recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();
    private recaptchaErrorMessage: string | undefined;
    
    public async componentDidMount(): Promise<void> {
        const items = this.getItems();
        const childIds = items.map((item: React.ReactNode) => get(item, 'props.id'));
        const shouldEnableSinglePaymentAuthorizationCheckout: boolean =
        this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout ?? false;

        if(this.props.config.reCaptchaOption){
            FingerprintJS.load({
                apiKey: "SavNfJLiPxQvtGc0eGD5",
                endpoint: [
                  "https://fp.cittadesign.com",
                  FingerprintJS.defaultEndpoint
                ],
                scriptUrlPattern: [
                  "https://fp.cittadesign.com/web/v<version>/<apiKey>/loader_v<loaderVersion>.js",
                  FingerprintJS.defaultScriptUrlPattern
                ]
              })
              .then(fpClient => this.setState({ fpClient }))
              .catch(error => console.error('FingerprintJS initialization failed:', error));
        }
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: shouldEnableSinglePaymentAuthorizationCheckout ? undefined : this.onCancel,
            onSubmit: shouldEnableSinglePaymentAuthorizationCheckout ? undefined : this.onSubmit,
            childIds,
        }
        );

        reaction(
            () => this.props.moduleState.isReady,
            () => {
                this.onContainerReady();
            }
        );

        reaction(
            () => this.props.moduleState.shouldSubmitContainer,
            () => {
                this.onSubmit();
            }
        );

        // Reaction to collapse the payment section for new checkout flow.
        reaction(
            () => this.props.data.checkout.result?.shouldCollapsePaymentSection,
            shouldCollapsePaymentSection => {
                const isPaymentSectionContainer = ArrayExtensions.hasElements(
                    this.props.moduleState.childIds.filter(childId => childId.includes(paymentInstrumentModuleId))
                );

                if (shouldEnableSinglePaymentAuthorizationCheckout && isPaymentSectionContainer) {
                    this.setState({ isCollapse: shouldCollapsePaymentSection });
                }
            }
        );

        // Reaction to submit section container for new checkout flow
        reaction(
            () => this.props.data.checkout.result?.shouldTriggerPaymentWithPlaceOrder,
            () => {
                const isPaymentSectionContainer = ArrayExtensions.hasElements(
                    this.props.moduleState.childIds.filter(childId => childId.includes(paymentInstrumentModuleId))
                );

                if (shouldEnableSinglePaymentAuthorizationCheckout && isPaymentSectionContainer) {
                    this.onSubmit();
                }
            }
        );

        // Reaction to set isPaymentSectionContainerReady for new checkout flow.
        reaction(
            () => this.props.moduleState.isReady,
            async isReady => {
                const isPaymentSectionContainer = ArrayExtensions.hasElements(
                    this.props.moduleState.childIds.filter(childId => childId.includes(paymentInstrumentModuleId))
                );

                if (shouldEnableSinglePaymentAuthorizationCheckout && isPaymentSectionContainer && this.amountDue > zeroAmount) {
                    await this.props.data.checkout.result?.updateIsPaymentSectionContainerReady({
                        newIsPaymentSectionContainerReady: isReady
                    });
                }
            }
        );

        // Reaction to set isPaymentSectionContainerHasError for new checkout flow.
        reaction(
            () => this.props.moduleState.hasError,
            async hasError => {
                const isPaymentSectionContainer = ArrayExtensions.hasElements(
                    this.props.moduleState.childIds.filter(childId => childId.includes(paymentInstrumentModuleId))
                );

                if (shouldEnableSinglePaymentAuthorizationCheckout && isPaymentSectionContainer) {
                    await this.props.data.checkout.result?.updateIsPaymentSectionContainerHasError({
                        newIsPaymentSectionContainerHasError: hasError
                    });
                }
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isCheckoutCallFailed,
            async () => {
                const isPaymentSectionContainer = ArrayExtensions.hasElements(
                    this.props.moduleState.childIds.filter(childId => childId.includes(paymentInstrumentModuleId))
                );

                if (isPaymentSectionContainer && this.props.data.checkout.result?.isCheckoutCallFailed) {
                    this.onEdit();
                    await this.props.data.checkout.result.updateIsCheckoutCallFailed({ newIsCheckoutCallFailed: true });
                }
            }
        );
    }

    public componentDidUpdate(): void {
        if (!this.props.moduleState.hasExternalSubmitGroup) {
            return;
        }
        /**
         * Review all modules to updating status if one of the modules has error
         */
        const { childIds, hasError, getModule } = this.props.moduleState;
        const hasReadyOrSkippedItems = childIds.some((childId: string) => {
            const state = getModule(childId);
            return !!state && (state.isReady || state.isSkipped);
        });
        if (hasReadyOrSkippedItems && hasError) {
            this.onEdit();
        }
    }

    public shouldComponentUpdate(nextProps: ICheckoutSectionContainerModuleProps, nextState: ICheckoutSectionContainerState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const {
            enableControl,
            moduleState,
            config: { width, paymentAccordion, creditCardIcon }
        } = this.props;

        const items = this.getItems();
        if (items.length === 0) {
            this.props.context.telemetry.error('Checkout section content is empty, module wont render');
            return null;
        }

        const moduleClassName = classname(
            'ms-checkout-section-container',
            {
                'has-initialized': moduleState.hasInitialized,
                'is-ready': moduleState.isReady,
                'is-updating': !moduleState.isReady,
                container: width === 'container',
                'col-12 fullwidth': width === 'fluid'
            },
            this.props.config.className
        );

        const viewProps: ICheckoutSectionContainerViewProps = {
            ...this.props,

            className: moduleClassName,
            reCaptcha: this._renderReCaptcha(),
            fingerPrintError: this._renderFingerPrintErrorMessage(),
            attemptError: this._renderAttemptError(),
            checkoutPlainContainerProps: {
                moduleProps: this.props,
                className: moduleClassName
            },
            items: items.map((item: React.ReactNode) => {
                const childId = get(item, 'props.id');
                const childTypeName = get(item, "props.typeName");
                const state = moduleState.getModule(childId);
                const customerInfo = get(this.props, 'data.customerInformation.result');
                const platform = get(this.props, 'context.request.app.platform');
                const isAllowOnAccountPayment = customerInfo && customerInfo.AllowOnAccountPayment && platform &&
                    (platform.enableCustomerAccountPayment === 'all' ||
                        (platform.enableCustomerAccountPayment === 'b2b' && customerInfo.IsB2b === true) ||
                        (platform.enableCustomerAccountPayment === 'b2c' && customerInfo.IsB2b === false))

                // Modules has not completed initializing or it's disable 
                const hidden = 
                     this.state.isCollapse || (state.hasModuleState && (!state.hasInitialized || state.isDisabled || state.isSkipped) || (isAllowOnAccountPayment && paymentAccordion ? this.state.activeAccordion !== childTypeName : ''));

                return {
                    id: childId,
                    loading: state.hasModuleState && !state.hasInitialized && <LoadingComponent />,
                    itemProps: {
                        'data-type-name': state.typeName,
                        className: classname('ms-checkout-section-container__item', {
                            hidden,
                            initialized: state.hasInitialized,
                            disabled: state.isDisabled,
                            skipped: state.isSkipped,
                            ready: state.isReady,
                            updating: state.isUpdating,
                            pending: state.isPending
                        })
                    },
                    accordion: isAllowOnAccountPayment && paymentAccordion ? this._renderAccordionItem(item, creditCardIcon) : null,
                    item: React.cloneElement(item as React.ReactElement, { enableControl })
                };
            })
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private getItems = (): React.ReactNode[] => {
        const { slots } = this.props;
        return (slots && slots.primary && slots.primary.length && slots.primary) || [];
    };

    private getActiveChildModuleStates = (): IModuleStateManager[] => {
        const { childIds, getModule } = this.props.moduleState;
        return childIds
            .map(getModule)
            .filter((state): state is IModuleStateManager => !!state && !state.isDisabled && state.hasModuleState);
    };

    private clearError = (): void => {
        // Clear error before action, otherwise it will rise conflict;
        this.getActiveChildModuleStates().forEach(state => {
            state.hasError && state.setHasError(false);
        });
    };

    private _renderReCaptcha(): React.ReactNode {
        if (this.props.config.reCaptchaOption) {
            return (
                <div>
                    <div className=''>
                        {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}
                        <ReCAPTCHA
                            ref={this._recaptchaRef}
                            sitekey={this.props.config.reCaptchaSiteKey!}
                            size="invisible"
                            onChange={() => this._submit()}
                        />
                    </div>
                </div>
            );
        }
        return null;
    }

    private _renderFingerPrintErrorMessage(): React.ReactNode {
        if (this.state.fingerPrintError) {
            return (
                <div className="ms-checkout-payment-instrument__error finger-print-error">
                    <p className='ms-checkout-payment-instrument__error-title'>Error message</p>
                    <p className='ms-checkout-payment-instrument__error-message'>
                        {this.props.config.fingerPrintErrorMessage}
                    </p>
                </div>
            )
        }
        return null
    }

    private _renderAttemptError(): React.ReactNode {
        if (this.state.attemptError && this.props.config.turnOnRateLimit) {
            return (
                <div className="ms-checkout-payment-instrument__error attempt-error">
                    <p className='ms-checkout-payment-instrument__error-title'>Error message</p>
                    <p className='ms-checkout-payment-instrument__error-message'>
                        {this.props.config.attemptErrorMessage}
                    </p>
                </div>
            )
        }
        return null
    }

    private _renderRecaptchaAlert(className: string): JSX.Element {

        if (this.props.config.reCaptchaOption) {
            if (!this.state.reCaptchaChecked) {
                this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
            }
        }

        const rCCError = this.state.rCCError;
        return (
            <>
                {rCCError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.recaptchaErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.recaptchaErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    private async _submit(): Promise<void> {
        const isPaymentSectionContainer = ArrayExtensions.hasElements(
            this.props.moduleState.childIds.filter(childId => childId.includes(paymentInstrumentModuleId))
        );

        this.props.telemetry.information('Section container onSubmit is called.');

        const MAX_ATTEMPTS = this.props.config.attemptThreshold || 5;
        const STORAGE_KEY = "paymentAttempts";
        const storedAttempts = localStorage.getItem(STORAGE_KEY);
        let attempts = storedAttempts ? JSON.parse(storedAttempts) : { count: 0, timestamp: Date.now() };
        if(attempts.count >= MAX_ATTEMPTS){
            const timeDiff = Date.now() - attempts.timestamp;
            if (timeDiff < 24 * 60 * 60 * 1000) {
                this.setState({
                    attemptError: true
                });
            } else {
                this.setState({
                    attemptError: false
                });
                attempts = { count: 0, timestamp: Date.now() };
            }
        }

        const recaptchaValue = this._recaptchaRef.current?.getValue();

        const { fpClient } = this.state;
        if (!fpClient) {
            console.error('FingerprintJS client not initialized');
            return;
          }
          try {
            const result = await fpClient.get();
            this.setState({ requestId: result.requestId });
          } catch (error) {
            console.error('Fingerprint collection failed:', error);
          }

        const res = this.props.config.turnOnFingerPrint && await MsDyn365.commerceApiRequest(
            this.props.context.request,
            'GetFingerprint',
            'post',
            {
                myInputValue: this.state.requestId
            }
        ) as any;
        const suspectScore: number = this.props.config.turnOnFingerPrint && res.data.result && res.data.result.data.result || 0;
        const suspectScoreThreshold: number = this.props.config.suspectScoreThreshold || 90;
        const isBlocked: boolean = suspectScore > suspectScoreThreshold;

        if (!recaptchaValue) {
            this.setState({
                rCCError: true,
                reCaptchaChecked: false
            });
            this._recaptchaRef.current?.reset();
        }
        else if (this.props.config.turnOnFingerPrint && isBlocked) {
            this.setState({
                fingerPrintError: true
            })
            this._recaptchaRef.current?.reset();
        }
        else if (this.props.config.turnOnRateLimit && this.state.attemptError) {
            this._recaptchaRef.current?.reset();
        } else {
            this.clearError();
            this.getActiveChildModuleStates().forEach(state => {
                !state.isReady && state.onSubmit && state.onSubmit();
                if (state.typeName === PaymentType.Adyen && !state.isReady) {
                    attempts.count++;
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
                }
                else if (state.typeName === PaymentType.Adyen && state.isReady) {
                    localStorage.removeItem(STORAGE_KEY);
                    this.setState({
                        attemptError: false
                    });
                }
            });

             // Handle when the amount due is zero
        if (
            this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout &&
            isPaymentSectionContainer &&
            this.amountDue === zeroAmount
        ) {
            this.props.data.checkout.result?.updateIsPaymentSectionContainerReady({
                newIsPaymentSectionContainerReady: true
            });
        }
        
            this._recaptchaRef.current?.reset();
        }
    }

    private _renderAccordionItem(item: any, creditCardIcon: any): React.ReactNode {
        const childTypeName = get(item, 'props.typeName');
        let slotName: string = '';
        switch (childTypeName) {
            case PaymentType.PayByAccount:
                slotName = 'Charge to Account';
                break;
            case PaymentType.Adyen:
                slotName = 'Credit Card';
                break;
            default: null
        }
        return (
            <div className={`accordion-item ${this.props.moduleState.isReady && childTypeName !== this.state.activeAccordion ? 'border-hidden' : ''}`} style={{ "display": this.props.moduleState.isReady && childTypeName !== this.state.activeAccordion ? "none" : "block" }}>
                <input type="radio" className="accordion-item__Radio" name={slotName} checked={childTypeName === this.state.activeAccordion} onChange={() => this.updateAccordion(childTypeName)} />
                <div className="accordion-item__Header">{slotName}</div>
                {childTypeName === PaymentType.Adyen ? <div className='credit-card-icon-wrap'><img className='credit-card-icon' src={creditCardIcon} alt="" /></div> : null}
            </div>
        )
    }

    public updateAccordion(newValue: string): void {
        if (this.state.activeAccordion === PaymentType.PayByAccount) {
            this.props.moduleState.getModuleByTypeName(this.state.activeAccordion).onSkip();
        }
        this.setState({ activeAccordion: newValue });
        this.props.moduleState.getModuleByTypeName(newValue).onUpdating();
        if (newValue === PaymentType.PayByAccount) {
            this.props.moduleState.getModuleByTypeName(PaymentType.Adyen).onDisable()
        } else {
            this.props.moduleState.getModuleByTypeName(PaymentType.PayByAccount).onDisable()
        }
    }

    private readonly onSubmit = (): void => {
        if (this.props.config.reCaptchaOption) {
            this._recaptchaRef.current?.execute()
        } else {
            const isPaymentSectionContainer = ArrayExtensions.hasElements(
                this.props.moduleState.childIds.filter(childId => childId.includes(paymentInstrumentModuleId))
            );
    
            this.props.telemetry.information('Section container onSubmit is called.');

            this.clearError();
            this.getActiveChildModuleStates().forEach(state => {
                !state.isReady && state.onSubmit && state.onSubmit();
            });

            // Handle when the amount due is zero
        if (
            this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout &&
            isPaymentSectionContainer &&
            this.amountDue === zeroAmount
        ) {
            this.props.data.checkout.result?.updateIsPaymentSectionContainerReady({
                newIsPaymentSectionContainerReady: true
            });
        }
        }
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('Section container onCancel is called.');

        this.clearError();
        this.getActiveChildModuleStates().forEach(state => {
            !state.isReady && state.onCancel && state.onCancel();
        });
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('Section container onEdit is called.');

        const {
            config: { paymentAccordion }, data
        } = this.props;
        const isAllowOnAccountPayment = data.customerInformation.result?.AllowOnAccountPayment;

        this.getActiveChildModuleStates().forEach(state => {
            (state.isReady || state.isSkipped) && state.onEdit && state.onEdit();
        });

        isAllowOnAccountPayment && paymentAccordion && this.props.moduleState.getModuleByTypeName(PaymentType.Adyen).onDisable()
    };

    private readonly onContainerReady = (): void => {
        this.props.telemetry.information('Section container onContainerReady is called.');
        
        this.getActiveChildModuleStates().forEach(state => {
            state.isReady && state.onContainerReady && state.onContainerReady();
        });
    };
}

export default withModuleState(CheckoutPlainContainer);
